/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { isEmpty } from '@ember/utils';
import { get, set, action, computed } from '@ember/object';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import { service } from '@ember/service';
import LocationType from '@linc-technologies/linced-common/enums/location-type';
import NSWStatus from '@linc-technologies/linced-common/enums/nsw-status';
import NSWSource from '@linc-technologies/linced-common/enums/nsw-source';
import SIFYesOrNo from '@linc-technologies/linced-common/enums/sif-yes-or-no';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class LocationsController extends Controller {
  NSWSource = NSWSource;
  NSWStatus = NSWStatus;

  // Services
  @service
  session;

  @service
  store;

  @service
  toaster;

  @tracked
  filters = [];
  @tracked
  locationToEdit = null;
  @tracked
  locationToDelete = null;
  @tracked
  locationToRestore = null;

  @computed('model.locations.@each.status', 'filters.@each.{field,value}')
  get filteredLocations() {
    let filteredLocations = this.model.locations;

    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return filteredLocations;
    }

    // Determine datatypes for any specified filters.
    let filters = this.filters.map((filter) => {
      let dataType = DataType.STRING;
      return {
        key: filter.field,
        dataType,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    return filteredLocations.filter((row) => {
      return filters.every((filter) => {
        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  closeFunc() {
    this.locationToEdit = null;
    this.locationToDelete = null;
    this.locationToRestore = null;
  }

  @action
  newLocation() {
    let newLocation = {
      schoolId: this.get('session.activeSchool'),
      name: 'New Location',
      description: 'What is this room for?',
      size: 10,
      capacity: 10,
      type: LocationType.CLASSROOM,
      status: NSWStatus.ACTIVE,
      source: NSWSource.VENDOR,
      canBeTimetabled: SIFYesOrNo.YES,
    };
    newLocation = this.store.createRecord('schools/location', newLocation);
    this.locationToEdit = newLocation;
  }

  @action
  editLocation(location) {
    this.locationToEdit = location;
  }

  @action
  deleteLocation(location) {
    this.locationToDelete = location;
  }

  @action
  restoreLocation(location) {
    this.locationToRestore = location;
  }

  @action
  closeDialog() {
    this.closeFunc();
  }

  @action
  saveAndCloseDialog(locationToSave) {
    locationToSave
      .save()
      .then(() => {
        return this.model.locations.update();
      })
      .then(() => {
        this.closeFunc();
      })
      .catch(() => {
        this.toaster.error('Error saving location');
      });
  }

  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }
}
